import { useState } from "react";
import { apiCall } from "../api";
import { useAuth } from "../context/AuthContext";
import { AuthLayout } from "./AuthLayout";
import { TerminosModal } from "./TerminosModal";
import { getSignupSource, trackEvent } from "../analytics";
import { submitVerification, VerificationFields } from "./Verification";

// Register Component
const Register = ({ onSwitch }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [showTerminos, setShowTerminos] = useState(false);
  const [quiereEvaluar, setQuiereEvaluar] = useState(false);
  const [verificationFiles, setVerificationFiles] = useState({ idFront: null, idBack: null, selfie: null });
  const { login, startGuestMode } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!aceptaTerminos) {
      setError('Debes aceptar los Terminos y Condiciones para registrarte.');
      setLoading(false);
      return;
    }

    if (quiereEvaluar && (!verificationFiles.idFront || !verificationFiles.selfie)) {
      setError('Para evaluar inquilinos sube la foto de tu identificaci\u00f3n y una selfie, o desmarca la opci\u00f3n.');
      setLoading(false);
      return;
    }

    try {
      const result = await apiCall('/auth/register', 'POST', { ...formData, signup_source: getSignupSource() });
      trackEvent('sign_up');
      let landlord = result.landlord;
      if (quiereEvaluar) {
        try {
          const verification = await submitVerification(verificationFiles, result.token);
          landlord = { ...landlord, verification_status: verification.verification_status };
        } catch (verificationError) {
          // The account exists; the landlord can retry from the "Evaluar Inquilino" tab
          console.error('Verification failed:', verificationError);
        }
      }
      login(landlord, result.token);
    } catch (err) {
      setError(err.detail || 'Error de registro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Crea tu cuenta" subtitle="Toma menos de un minuto." onHome={() => onSwitch('landing')}>
        <form onSubmit={handleSubmit} className="text-left space-y-5">
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Nombre Completo</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              placeholder="Juan P&eacute;rez Gonz&aacute;lez"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Correo electr&oacute;nico</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              placeholder="juan@inmobiliaria.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Contrase&ntilde;a</label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              placeholder=""
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Empresa/Inmobiliaria <span className="text-amber-600 font-normal">(Opcional)</span></label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
              className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              placeholder="Inmobiliaria P&eacute;rez S.L."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Tel&eacute;fono Profesional <span className="text-amber-600 font-normal">(Opcional)</span></label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
              placeholder="+34 123 456 789"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="quiere-evaluar"
                checked={quiereEvaluar}
                onChange={(e) => setQuiereEvaluar(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-500 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="quiere-evaluar" className="text-sm text-gray-700 leading-snug cursor-pointer">
                <span className="font-semibold">Quiero evaluar inquilinos.</span> Requiere una selfie y una foto de tu identificaci&oacute;n oficial. Tambi&eacute;n puedes hacerlo despu&eacute;s.
              </label>
            </div>
            {quiereEvaluar && <VerificationFields files={verificationFiles} setFiles={setVerificationFiles} />}
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg shadow-sm">
              <div className="flex">
                <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">{error}</span>
              </div>
            </div>
          )}

          {showTerminos && <TerminosModal onClose={() => setShowTerminos(false)} />}

          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <input
              type="checkbox"
              id="terminos"
              checked={aceptaTerminos}
              onChange={(e) => setAceptaTerminos(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-amber-500 cursor-pointer flex-shrink-0"
            />
            <label htmlFor="terminos" className="text-sm text-gray-700 leading-snug cursor-pointer">
              He le&iacute;do y acepto los{' '}
              <button
                type="button"
                onClick={() => setShowTerminos(true)}
                className="text-amber-600 font-semibold underline hover:text-amber-700"
              >
                T&eacute;rminos y Condiciones
              </button>
              {' '}y el{' '}
              <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-amber-600 font-semibold underline hover:text-amber-700">
                Aviso de Privacidad
              </a>
              . Entiendo que conocetuinquilino.com es solo un intermediario y acepto toda la responsabilidad legal por el contenido que publique.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3.5 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 shadow-md disabled:opacity-50 font-bold text-base transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creando Cuenta...
              </div>
            ) : 'Crear cuenta'}
          </button>
        </form>

        <div className="mt-8 text-center space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="border-t border-amber-200 flex-1"></div>
            <span className="px-4 text-sm text-amber-700/80">&iquest;Solo quieres probar?</span>
            <div className="border-t border-amber-200 flex-1"></div>
          </div>
          
          {/* Consulta Rpida Button */}
          <button
            onClick={startGuestMode}
            className="w-full bg-white text-amber-900 py-3 px-6 rounded-lg border border-amber-200 hover:bg-amber-50 font-semibold transition-colors mb-2"
          >
            <div className="flex items-center justify-center space-x-3">
              <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Consulta r&aacute;pida</span>
            </div>
          </button>
          
          <p className="text-xs text-amber-700/80 mb-4">
            Accede sin cuenta para hacer consultas b&aacute;sicas. Funciones limitadas.
          </p>
          
          <div className="flex items-center justify-center mb-4">
            <div className="border-t border-amber-200 flex-1"></div>
            <span className="px-4 text-sm text-amber-700/80">&iquest;Ya tienes cuenta?</span>
            <div className="border-t border-amber-200 flex-1"></div>
          </div>
          <button
            onClick={() => onSwitch('login')}
            className="text-amber-800 hover:text-amber-900 font-semibold hover:underline"
          >
            Iniciar sesi&oacute;n
          </button>
        </div>
    </AuthLayout>
  );
};

export { Register };
